import React, { useState } from "react";
import PropTypes from "prop-types";

const DashboardSearch = ({
  items,
  searchKeys = ["name"],
  placeholder = "Search...",
  onItemClick,
  getItemLabel,
  getItemSubLabel,
  getItemBadge,
  onFocus,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = React.useRef(null);

  // Filter items
  const filteredItems = items.filter((item) => {
    if (!searchTerm) return false;
    const term = searchTerm.toLowerCase();
    return searchKeys.some((key) => {
      const val = item[key];
      return val?.toString().toLowerCase().includes(term);
    });
  });

  const isSearching = searchTerm.length > 0;

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="search-container"
      style={{ position: "relative", width: "300px" }}
    >
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => {
          setShowDropdown(true);
          if (onFocus) onFocus();
        }}
        // Removed onBlur - relying on click outside
        className="dashboard-search-input"
        style={{
          width: "100%",
          padding: "10px 12px 10px 38px",
          borderRadius: "20px",
          border: "1px solid var(--color-border)",
          fontSize: "0.9rem",
        }}
      />

      {/* Dropdown Results */}
      {showDropdown && isSearching && (
        <div
          className="search-dropdown"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "8px",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            zIndex: 9999,
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {filteredItems.length === 0 && (
            <div
              style={{ padding: "12px", color: "var(--color-text-secondary)" }}
            >
              No results found.
            </div>
          )}
          {filteredItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="dropdown-item-btn"
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--color-bg-subtle)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
              onClick={() => {
                onItemClick(item);
                setSearchTerm("");
                setShowDropdown(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: "transparent",
                textAlign: "left",
                cursor: "pointer",
                transition: "background 0.2s",
                borderBottom: "1px solid var(--color-border)",
              }}
            >
              <div style={{ flex: 1, pointerEvents: "none" }}>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "0.95rem",
                  }}
                >
                  {getItemLabel(item)}
                </div>
                {getItemSubLabel && (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--color-text-secondary)",
                    }}
                  >
                    {getItemSubLabel(item)}
                  </div>
                )}
              </div>
              {getItemBadge?.(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

DashboardSearch.propTypes = {
  items: PropTypes.array.isRequired,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  onItemClick: PropTypes.func.isRequired,
  getItemLabel: PropTypes.func.isRequired,
  getItemSubLabel: PropTypes.func,
  getItemBadge: PropTypes.func,
  onFocus: PropTypes.func,
};

export default DashboardSearch;
